import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useTranslation } from 'react-i18next';

/**
 * Hook personnalisé pour gérer la navigation vers les profils utilisateurs
 * @param {Object} user - L'utilisateur cible vers lequel naviguer
 * @returns {Object} - Fonctions et états liés à la navigation vers le profil
 */
export const useProfileNavigation = (user) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation('common');

  // Détermine si l'utilisateur courant peut naviguer vers le profil cible
  const canNavigate = useMemo(() => {
    if (!user || !currentUser) {
      return false;
    }
    
    // Si c'est l'utilisateur lui-même, il peut toujours voir son profil
    if (currentUser.uid === user.uid) {
      return true;
    }
    
    // Dans le contexte des followers/following, on devrait pouvoir naviguer vers tous les profils
    // car il y a déjà une relation établie
    const profileVisibility = user.preferences?.profileVisibility || 'everyone';
    
    // Si le profil est visible par tout le monde
    if (profileVisibility === 'everyone') {
      return true;
    }
    
    // Si le profil est visible par les abonnés seulement
    if (profileVisibility === 'followers') {
      const canNavigateToFollower = currentUser.following?.includes(user.uid);
      return canNavigateToFollower;
    }
    
    // Pour les profils privés, dans le contexte followers/following, on devrait quand même pouvoir naviguer
    // car la relation existe déjà
    return true;
  }, [user, currentUser]);

  // Détermine si le profil cible est celui de l'utilisateur courant
  const isOwnProfile = useMemo(() => {
    return currentUser?.uid === user?.uid;
  }, [currentUser, user]);

  // Fonction pour naviguer vers le profil
  const handleNavigate = () => {
    if (!canNavigate) return;
    
    if (isOwnProfile) {
      // Si c'est le propre profil de l'utilisateur, naviguer vers la route sans paramètre
      navigate('/dashboard/profile');
    } else if (user?.uid) {
      // Sinon, naviguer vers le profil spécifique
      navigate(`/dashboard/profile/${user.uid}`);
    }
  };

  // Retourner les valeurs et fonctions utiles
  return {
    canNavigate,
    handleNavigate,
    isOwnProfile,
    navigationTitle: canNavigate 
      ? t('actions.view_profile') 
      : t('actions.profile_restricted')
  };
}; 